import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from "react";
import type {
  ColDef,
  ColumnState,
  DragStartedEvent,
  DragStoppedEvent,
  HeaderClass
} from "ag-grid-community";
import { useColumnDragAutoScroll } from "./useColumnDragAutoScroll";

/**
 * Drag-to-reorder for AG Grid column headers (CRM-1891), the way the Analytics table works:
 * press and drag a header, the other columns slide aside, the grid scrolls near its edges, and
 * the new order is reported once on drop. A drop outside the table puts the columns back.
 *
 * Only columns listed in `order` move. Columns pinned to an edge stay fixed, and nothing can be
 * dropped among them (`lockPinned`). Columns the caller renders but doesn't list (selection
 * checkboxes, row actions) stay attached to the column they follow in the defs.
 */

const MOVABLE_HEADER_CLASS = "boom-col-movable";

type ColumnGroup<T> = { orderIndex: number; defs: ColDef<T>[] };

const withMovableClass = <T,>(headerClass: HeaderClass<T> | undefined) => {
  if (!headerClass) return MOVABLE_HEADER_CLASS;
  if (typeof headerClass === "string")
    return `${headerClass} ${MOVABLE_HEADER_CLASS}`;
  if (Array.isArray(headerClass)) return [...headerClass, MOVABLE_HEADER_CLASS];
  return headerClass;
};

// AG Grid falls back to `field` when a def has no `colId`; do the same so such columns can move.
const columnId = <T,>(def: ColDef<T>) =>
  def.colId ?? (typeof def.field === "string" ? def.field : undefined);

const isMovable = <T,>(def: ColDef<T>, rank: Map<string, number>) => {
  const id = columnId(def);
  return Boolean(id && rank.has(id) && !def.pinned && !def.lockPosition);
};

/** Arrange column defs in `order` and mark the reorderable ones as movable. */
export const arrangeColumnDefs = <T,>(
  defs: ColDef<T>[],
  order: string[]
): ColDef<T>[] => {
  const rank = new Map(order.map((id, index) => [id, index]));
  const groups: ColumnGroup<T>[] = [{ orderIndex: -1, defs: [] }];

  defs.forEach((def) => {
    if (!isMovable(def, rank)) {
      groups[groups.length - 1].defs.push({ ...def, suppressMovable: true });
      return;
    }
    groups.push({
      orderIndex: rank.get(columnId(def) ?? "") ?? Number.MAX_SAFE_INTEGER,
      defs: [
        {
          ...def,
          suppressMovable: false,
          lockPinned: true,
          headerClass: withMovableClass(def.headerClass)
        }
      ]
    });
  });

  // Fixed columns after the last reorderable one (a trailing "fill" column, row actions) stay at
  // the end instead of following whichever column the user drags last.
  const last = groups[groups.length - 1];
  const tail = groups.length > 1 ? last.defs.splice(1) : [];

  const [head, ...rest] = groups;
  return [
    ...head.defs,
    ...rest
      .toSorted((a, b) => a.orderIndex - b.orderIndex)
      .flatMap((group) => group.defs),
    ...tail
  ];
};

type PointerPosition = { x: number; y: number };

const isInside = (element: HTMLElement | null, point: PointerPosition) => {
  if (!element) return true;
  const rect = element.getBoundingClientRect();
  return (
    point.x >= rect.left &&
    point.x <= rect.right &&
    point.y >= rect.top &&
    point.y <= rect.bottom
  );
};

const sameOrder = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

const scopeAttribute = (id: string) => id.replace(/[^a-zA-Z0-9_-]/g, "");

type Options = {
  /** Reorderable column ids, in the order the table shows them. */
  order: string[];
  /** Called once per drop, with the ids that are on screen in their new order. */
  onReorder: (visibleOrder: string[]) => void;
};

export const useColumnReorder = ({ order, onReorder }: Options) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scope = scopeAttribute(useId());
  const [movingColId, setMovingColId] = useState<string | null>(null);
  const stateAtStartRef = useRef<ColumnState[] | null>(null);
  const pointerRef = useRef<PointerPosition | null>(null);

  // AG Grid reports the drop after the pointer is released, so remember where that happened.
  useEffect(() => {
    if (!movingColId) return;
    const track = (event: PointerEvent) => {
      pointerRef.current = { x: event.clientX, y: event.clientY };
    };
    window.addEventListener("pointermove", track, true);
    window.addEventListener("pointerup", track, true);
    return () => {
      window.removeEventListener("pointermove", track, true);
      window.removeEventListener("pointerup", track, true);
    };
  }, [movingColId]);

  const getViewport = useCallback(
    () =>
      containerRef.current?.querySelector<HTMLElement>(
        ".ag-center-cols-viewport"
      ) ?? null,
    []
  );
  useColumnDragAutoScroll(Boolean(movingColId), getViewport);

  const reorderable = useMemo(() => new Set(order), [order]);

  const onDragStarted = useCallback(
    (event: DragStartedEvent) => {
      const colId = event.target.closest("[col-id]")?.getAttribute("col-id");
      if (!colId || !reorderable.has(colId)) return;
      stateAtStartRef.current = event.api.getColumnState();
      pointerRef.current = null;
      setMovingColId(colId);
    },
    [reorderable]
  );

  const onDragStopped = useCallback(
    (event: DragStoppedEvent) => {
      const stateAtStart = stateAtStartRef.current;
      stateAtStartRef.current = null;
      setMovingColId(null);
      if (!stateAtStart) return;

      const visibleIds = (state: ColumnState[]) =>
        state
          .filter((column) => !column.hide && reorderable.has(column.colId))
          .map((column) => column.colId);
      const before = visibleIds(stateAtStart);
      const after = visibleIds(event.api.getColumnState());
      if (sameOrder(before, after)) return;

      const pointer = pointerRef.current;
      if (pointer && !isInside(containerRef.current, pointer)) {
        event.api.applyColumnState({ state: stateAtStart, applyOrder: true });
        return;
      }
      onReorder(after);
    },
    [reorderable, onReorder]
  );

  const highlight = movingColId ? (
    <style>{`[data-col-reorder="${scope}"] .ag-theme-boom [col-id="${CSS.escape(movingColId)}"] { background-color: #1c1d221a !important; } [data-col-reorder="${scope}"] .ag-theme-boom .ag-header-cell, [data-col-reorder="${scope}"] .ag-theme-boom .ag-header-cell * { cursor: grabbing !important; }`}</style>
  ) : null;

  return {
    containerProps: { ref: containerRef, "data-col-reorder": scope },
    gridProps: {
      onDragStarted,
      onDragStopped,
      suppressDragLeaveHidesColumns: true
    },
    highlight
  };
};
